<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import ClientReportService from '@/services/ClientReportService';
import LineChart from '@/components/echarts/LineChart.vue';
import PieChart from '@/components/echarts/PieChart.vue';
import router from '@/router';
import {formatAmount, formatPercents, formatDate} from '@/helper';
import {useRoute} from 'vue-router';
import {getLoggedUser, getToken, refreshLoggedUser} from '@/services/AuthService';
import ClientOnboardingOverlay from '@/components/modals/ClientOnboardingOverlay.vue';
import {jwtDecode} from "jwt-decode";
import {useI18n} from 'vue-i18n';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import DateRangeModal from '@/components/modals/DateRangeModal.vue';
import AppAnnouncements from '@/components/AppAnnouncements.vue';
import DashboardClientBonds from '@/components/dashboard/DashboardClientBonds.vue';
import ClientBondService from '@/services/ClientBondService';
import {areBondsEnabled} from '@/utils/features';


const {t} = useI18n()
const contracts = ref([]);
const stats = ref(null);        // původní "all period" stats
const rangeStats = ref(null);   // stats pro YTD / vybraný date range (3. karta)
const loadingMain = ref(true);
const loading = ref({
  composition: true
});
const compositionByStrategy = ref(null);
const compositionByAsset = ref(null);
const loggedClient = ref(null);
const loggedUser = ref(null);
const statsChartData = ref(null);
const statsChartDataCategories = ref(null);
const statsChartDataCurrency = ref(null);
const onboardingModalOpen = ref(false);
const pendingDeposits = ref([]);
const dateRange = ref({
  from: null,
  to: null
});
// Probíhá načítání hodnot pro vybrané období (3. karta) - během něj karta ukazuje skeleton.
const rangeLoading = ref(false);
// Pořadové číslo posledního requestu na range stats. Slouží k zahození pozdě doběhlých
// odpovědí, když uživatel rychle za sebou změní období víckrát.
let rangeRequestId = 0;

const query = useRoute().query;

// IMA-517 fáze 6: přepínač Investice × Dluhopisy. Zobrazí se jen klientovi, který dluhopisy má,
// nebo mu je nějaký dostupný (Q17). Souhrn dluhopisů se načítá zvlášť, aby chyba nebo pomalost
// přehledu dluhopisů nezdržela stávající dashboard investic.
const activeView = ref('INVESTMENTS');
const bondStats = ref(null);
const bondsVisited = ref(false);
watch(activeView, view => {
  if (view === 'BONDS') {
    bondsVisited.value = true;
  }
});
const showBondsSwitch = computed(() => !!bondStats.value && (bondStats.value.has_bonds || bondStats.value.has_available));


function getCurrentYearYtdRange() {
  const today = new Date();
  today.setDate(today.getDate() - 1); // včerejšek
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return {
    from: `${year}-01-01`,
    to: `${year}-${month}-${day}`,
  };
}

onMounted(async () => {
  // souhrn dluhopisů nezávisle na reportingu investic – rozhoduje jen o přepínači Investice × Dluhopisy;
  // v instanci bez dluhopisů (B.I.G. Invest SK/CZ) se souhrn nenačítá a přepínač se nezobrazí
  areBondsEnabled().then(enabled => enabled ? ClientBondService.getStats() : null).then(response => {
    bondStats.value = response;
  }).catch(() => {
    // bez souhrnu dluhopisů přepínač nezobrazíme, dashboard investic funguje dál
    bondStats.value = null;
  });

  // Kontrola čekajících požadavků (žlutý box / onboarding overlay) musí běžet nezávisle
  // na reportingu. Dříve se volala až po načtení statistik, takže když reporting selhal
  // (typicky klient v onboardingu, který ještě nemá investice), overlay se vůbec neotevřel,
  // přestože profil čekající formuláře/dotazníky obsahoval.
  getLoggedUser().then(response => {
    if (response.role === 'CLIENT') {
      loggedClient.value = response.client;
      loggedUser.value = response;
      // if logged client has some yellow box, open onboarding modal
      // IMA-511: read-only klienta nesmíme nutit do onboarding wizardu, který stejně nemůže
      // dokončit (backend by odeslání kroku zamítl 403) - jen by ho to uvěznilo v appce.
      if (hasYellowBox() && (!impersonator() || query.ignore_impersonation === 'true') && !response.client?.is_read_only) {
        onboardingModalOpen.value = true;
      }
    }
  });

  emitter.on('onboardingDone', () => {
    onboardingModalOpen.value = false;
    void refreshLoggedUser();
  });

  // Všechny requesty paralelně — včetně obou stats (all-time i YTD) najednou,
  // aby nedošlo k race condition ani zbytečnému sekvenčnímu čekání.
  // allSettled místo all: selhání jednoho reportu nesmí shodit celý dashboard
  // (nekonečný skeleton) - neúspěšné části se jen nezobrazí.
  const [contractsRes, allTimeStatsRes, ytdStatsRes, historyRes, cmpstByStrategyRes, cmpstByAssetRes] = await Promise.allSettled([
    ClientReportService.getAllContracts(),
    ClientReportService.getStats(),
    ClientReportService.getStats(getCurrentYearYtdRange()),
    ClientReportService.getHistory(),
    ClientReportService.getComposition('strategy'),
    ClientReportService.getComposition('position'),
  ]);
  // Hodnota splněného promise, jinak null - šablona pak danou část přeskočí
  const valueOf = (result) => result.status === 'fulfilled' ? result.value : null;

  const allTimeStats = valueOf(allTimeStatsRes);
  // Historie může přijít i jako null/objekt (klient bez pozic) - map() voláme jen nad polem
  const historyData = Array.isArray(valueOf(historyRes)) ? valueOf(historyRes) : [];

  contracts.value = valueOf(contractsRes)?.data ?? [];
  stats.value = allTimeStats;
  rangeStats.value = valueOf(ytdStatsRes);
  pendingDeposits.value = allTimeStats?.pending_deposits ?? [];
  compositionByStrategy.value = valueOf(cmpstByStrategyRes)?.data ?? null;
  compositionByAsset.value = valueOf(cmpstByAssetRes)?.data ?? null;
  statsChartData.value = historyData.map(candle => candle.amount);
  statsChartDataCategories.value = historyData.map(candle => candle.date);
  statsChartDataCurrency.value = allTimeStats?.performance?.currency ?? null;

  loadingMain.value = false;
  loading.value = {
    ...loading.value,
    composition: false,
  };
})

function impersonator() {
  // getToken() vrací token aktivní pro tento panel - u impersonace jde o token
  // ze sessionStorage (localStorage nese pořád jen původní, neimpersonovanou identitu).
  const token = jwtDecode(getToken());
  if (!token || token.impersonator === null) {
    return false
  }
  return token.impersonator;
}

function countDataForChart(data) {
  return data.map(candle => [candle.date, candle.amount]);
}

function confirmServiceRequirement(id) {
  // Vede na read-only detail požadavku s možností přidat jen komentář — ne na formulář
  // pro založení/úpravu požadavku, který dřív umožňoval tiše přepsat původní zprávu.
  router.push('/my-service-requirements/' + id);
}

/**
 * Má klient něco ke zpracování (údaje k potvrzení, dokumenty k podpisu, čekající formuláře)?
 * Optional chaining: chybějící klíč v profilu nesmí vyhodnocení shodit výjimkou,
 * jinak by se overlay neotevřel ani při ostatních čekajících položkách.
 */
function hasYellowBox() {
  const user = loggedUser.value;
  return !!user?.meta?.show_form || user?.meta?.documents_to_sign > 0 || user?.waiting_meeting_records?.length > 0
      || user?.waiting_investment_questionnaires?.length > 0 || user?.waiting_aml_questionnaires?.length > 0
      || user?.waiting_forms?.length > 0 || user?.waiting_identifications?.length > 0;
}

/**
 * Otevře modal pro výběr období a po potvrzení přepočítá 3. kartu (výnos za období).
 *
 * - Modal se předvyplní aktuálně zobrazeným obdobím (výchozí YTD), ne prázdnými poli.
 * - minDate = datum první investice z API, aby nešlo zvolit období před založením smlouvy.
 * - dateRange (popisek karty) se přepíše až po úspěšné odpovědi - při chybě tak popisek
 *   i hodnoty zůstanou konzistentně na původním období.
 */
/**
 * Období, za které jsou zobrazené hodnoty 3. karty.
 * Přednost má období vrácené z API (rangeStats.range) - backend posouvá začátek na datum
 * první investice, takže u YTD s první investicí v průběhu roku to není 1.1. Fallback
 * (zvolené období / YTD) se použije jen u starší odpovědi API bez pole range.
 */
const effectiveRange = computed(() => {
  const fallback = (dateRange.value.from || dateRange.value.to) ? dateRange.value : getCurrentYearYtdRange();
  return {
    from: rangeStats.value?.range?.from ?? fallback.from,
    to: rangeStats.value?.range?.to ?? fallback.to,
  };
});

// Popisek karty; chybějící konec období (uživatel vyplnil jen jedno pole) zobrazíme jako "..."
const effectiveRangeLabel = computed(() => {
  const {from, to} = effectiveRange.value;
  return (from ? formatDate(from, 'd.m.Y') : '...') + ' - ' + (to ? formatDate(to, 'd.m.Y') : '...');
});

function openDateRangeModal() {
  const currentRange = effectiveRange.value;
  const {reveal, onConfirm} = createConfirmDialog(DateRangeModal, {
    fromDate: currentRange.from,
    toDate: currentRange.to,
    minDate: stats.value?.first_position_date ?? null,
  });
  reveal();
  onConfirm(async (data) => {
    const requestId = ++rangeRequestId;
    rangeLoading.value = true;

    try {
      // Graf (history) nechceme přepočítávat. Aktualizujeme pouze hodnoty pro 3. kartu (date range).
      const rangeToUse = (data.from || data.to) ? data : getCurrentYearYtdRange();
      const dataStats = await ClientReportService.getStats(rangeToUse);

      // Mezitím uživatel zvolil jiné období -> tato odpověď je zastaralá, nepoužijeme ji.
      if (requestId !== rangeRequestId) {
        return;
      }
      rangeStats.value = dataStats;
      dateRange.value = data;
    } catch (e) {
      // případný error necháme projít globálním handlerem http klienta;
      // dateRange ani rangeStats neměníme, karta dál ukazuje původní (platné) období
    } finally {
      if (requestId === rangeRequestId) {
        rangeLoading.value = false;
      }
    }
  });
}

</script>
<template>
  <div v-if="onboardingModalOpen">
    <ClientOnboardingOverlay></ClientOnboardingOverlay>
  </div>
  <div v-else>
    <div v-for="requirement of loggedUser?.service_requirements" class="row">
      <div class="col-12">
        <div @click="confirmServiceRequirement(requirement.id)" class="alert alert-warning d-flex justify-content-between cursor-pointer">
          <span>{{ t('dashboardClient.service_request_to_deal') }} {{ requirement.request_text || requirement.text }}</span>
          <u>{{ t('dashboardClient.retrieve') }}</u>
        </div>
      </div>
    </div>
    <!-- IMA-519: časově omezená oznámení (např. odstávka Bank iD) -->
    <AppAnnouncements/>
    <ul v-if="showBondsSwitch" class="nav--tabs nav nav--pills mb-4" role="tablist" :aria-label="t('clientBonds.switch_bonds')">
      <li class="nav-item col-6" role="presentation">
        <button type="button" class="nav-link" :class="{active: activeView === 'INVESTMENTS'}" role="tab" :aria-selected="activeView === 'INVESTMENTS'" @click="activeView = 'INVESTMENTS'">{{ t('clientBonds.switch_investments') }}</button>
      </li>
      <li class="nav-item col-6" role="presentation">
        <button type="button" class="nav-link" :class="{active: activeView === 'BONDS'}" role="tab" :aria-selected="activeView === 'BONDS'" @click="activeView = 'BONDS'">{{ t('clientBonds.switch_bonds') }}</button>
      </li>
    </ul>
    <!-- v-show: přehled investic zůstává vykreslený i s grafy, přepnutí zpět je okamžité -->
    <div v-show="activeView === 'INVESTMENTS'" class="page-section">
      <div class="page-section__heading">
        <div class="page-section__title">
          {{ t('dashboardClient.my_porfolio') }}
        </div>
      </div>
      <div class="page-section__content">
        <div v-if="loadingMain" class="text-center">
          <!-- <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ t('dashboardClient.loadingMain') }}</span>
          </div> -->
          <div class="row d-none d-lg-flex">
            <div class="col-md-3 text-center" v-for="i in 4" :key="i">
              <Skeletor width="100%" height="32" pill/>
              <Skeletor width="100%" height="22" pill class="mt-2"/>
            </div>
          </div>
          <div class="row d-lg-none">
            <div class="col-12">
              <Skeletor width="100%" height="32" pill/>
              <Skeletor width="100%" height="22" pill class="mt-2"/>
            </div>
          </div>

          <div class="row mt-5">
            <div class="col-12">
              <Skeletor width="100%" height="150"/>
            </div>
          </div>
        </div>
        <div v-if="!loadingMain">
          <!-- Statistiky zobrazit jen při úspěšně načtených datech (reporting může selhat, viz onMounted) -->
          <div v-if="stats?.performance" class="mb-4">
            <div class="row g-2 g-md-3 mb-3">
              <!-- Hodnota portfolia -->
              <div class="col-12 col-md-6 col-lg-3">
                <div class="card card--flat card--big-value h-100">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <div class="card__value__label">{{ t('dashboardClient.portfolio_value') }}</div>
                    <div class="card__value">
                      {{ formatAmount(stats.performance.totalAmount, stats.performance.currency) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Investováno — celková suma vložených prostředků -->
              <div class="col-12 col-md-6 col-lg-3">
                <div class="card card--flat card--big-value h-100">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <div class="card__value__label">{{ t('dashboardClient.invested') }}</div>
                    <div class="card__value">
                      {{ formatAmount(stats.performance.investedAmount, stats.performance.currency) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Výnos za celé období -->
              <div class="col-12 col-md-6 col-lg-3">
                <div class="card card--flat card--big-value h-100">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <div class="card__value__label">{{ t('dashboardClient.all_period') }}</div>
                    <div class="card__value" :class="{'ci ci--up': stats.performance.performancePerc > 0, 'ci ci--down': stats.performance.performancePerc < 0}">
                      <span>{{ stats.performance.gain > 0 ? '+' : '' }}</span>{{ formatAmount(stats.performance.gain, stats.performance.currency) }}
                    </div>
                    <div class="card__subvalue" :class="{'ci ci--up': stats.performance.performancePerc > 0, 'ci ci--down': stats.performance.performancePerc < 0}">
                      <span>{{ stats.performance.performancePerc > 0 ? '+' : '' }}</span>{{ formatPercents(stats.performance.performancePerc) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Date range karta — výběr vlastního období, výchozí YTD -->
              <div class="col-12 col-md-6 col-lg-3">
                <div class="card card--flat card--big-value h-100 cursor-pointer" @click="openDateRangeModal">
                  <div class="card-body py-0 d-flex flex-column justify-content-center">
                    <div class="card__value__label">
                      {{ effectiveRangeLabel }}
                      <i class="mdi mdi-pencil ms-1"></i>
                    </div>
                    <!-- Během přepočtu období ukazujeme skeleton, aby nebyly vidět hodnoty předchozího období -->
                    <template v-if="rangeLoading">
                      <Skeletor width="100%" height="32" pill/>
                      <Skeletor width="60%" height="22" pill class="mt-2"/>
                    </template>
                    <!-- YTD statistiky se načítají samostatným requestem - při jeho selhání kartu necháme bez hodnot -->
                    <template v-else-if="rangeStats?.performance">
                      <div class="card__value" :class="{'ci ci--up': rangeStats.performance.performancePerc > 0, 'ci ci--down': rangeStats.performance.performancePerc < 0}">
                        <span>{{ rangeStats.performance.gain > 0 ? '+' : '' }}</span>{{ formatAmount(rangeStats.performance.gain, rangeStats.performance.currency) }}
                      </div>
                      <div class="card__subvalue" :class="{'ci ci--up': rangeStats.performance.performancePerc > 0, 'ci ci--down': rangeStats.performance.performancePerc < 0}">
                        <span>{{ rangeStats.performance.performancePerc > 0 ? '+' : '' }}</span>{{ formatPercents(rangeStats.performance.performancePerc) }}
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-5" v-if="pendingDeposits.length > 0">
              <div class="col-12">
                <div v-for="deposit of pendingDeposits" :key="deposit.id" class="alert alert-info">
                  <div class="alert-icon">
                    <i class="mdi mdi-cash-multiple"></i>
                  </div>
                  <div class="alert-content" v-html="t('pendingDeposits.info_box_text', {label_strategy: deposit.label_strategy ?? '', label_contract: deposit.label_contract ?? '', amount_formatted: formatAmount(deposit.amount, deposit.currency)})">
                  </div>
                </div>
              </div>
            </div>


            <div class="row mt-5">
              <div class="col-12">
                <div class="card card--flat">
                  <div class="card-body card-body--only-chart">
                    <div class="card-body__chart">
                      <LineChart
                          :series="[
                            {
                              name: t('dashboardClient.portfolio_value'),
                              data: statsChartData
                            }
                          ]"
                          :valuesType="'amount'"
                          :categories="statsChartDataCategories"
                          :currency="statsChartDataCurrency"
                      ></LineChart>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="page-section mt-5">
        <div class="page-section__heading">
          <div class="page-section__title">
            {{ t('dashboardClient.my_contracts') }}
          </div>
        </div>
        <div class="page-section__content">
          <div v-if="loadingMain">
            <div class="row">
              <div class="col-12 col-lg-6 mb-2 mb-md-3" v-for="i in 2" :key="i">
                <Skeletor width="100%" height="76" pill/>
              </div>
            </div>
          </div>
          <div v-else>
            <div v-for="contract of contracts" v-bind:key="contract">
              <div @click="router.push('/contract/' + contract.id)" class="card cursor-pointer card-contract mb-2 mb-lg-3">
                <div class="card-header card-header--small">
                  <div class="row align-items-center">
                    <div class="col-auto">
                      <div class="card-header__title">{{ contract.contractNumber }}</div>
                      <div class="card-header__subtitle">{{ contract.contractType }}</div>
                    </div>
                    <div class="col text-end">
                      <div class="card-header__value">
                        <div class="">{{ formatAmount(contract.performance.totalAmount, contract.performance.currency) }}</div>
                        <div class="card-header__value__sub" :class="{'ci ci--up': contract.performance.performancePerc > 0, 'ci ci--down': contract.performance.performancePerc < 0}">
                          <span>{{ contract.performance.performancePerc > 0 ? '+' : '' }}</span>{{ formatPercents(contract.performance.performancePerc) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="page-section mt-5">
        <div class="page-section__heading">
          <div class="page-section__title">
            {{ t('dashboardClient.portfolio_layout') }}
          </div>
        </div>
        <div class="page-section__content">

          <ul class="nav--tabs nav nav--pills" id="pills-tab" role="tablist">
            <li class="nav-item col-6" role="presentation">
              <button class="nav-link active" id="dashboard-tab-composition-by-strategy" data-bs-toggle="pill" data-bs-target="#dashboard-tab-composition-by-strategy-content" type="button" role="tab" aria-controls="dashboard-tab-composition-by-strategy-content" aria-selected="true">{{ t('dashboardClient.according_to_strategy') }}</button>
            </li>
            <li class="nav-item col-6" role="presentation">
              <button class="nav-link" id="dashboard-tab-composition-by-assets" data-bs-toggle="pill" data-bs-target="#dashboard-tab-composition-by-assets-content" type="button" role="tab" aria-controls="dashboard-tab-composition-by-assets-content" aria-selected="true">{{ t('dashboardClient.according_to_position') }}</button>
            </li>
          </ul>

          <div class="tab-content" id="pills-tabContent">
            <div class="tab-pane fade show active" id="dashboard-tab-composition-by-strategy-content" role="tabpanel" aria-labelledby="dashboard-tab-composition-by-strategy">
              <div class="card">
                <div class="card-body card-body--only-chart" v-if="compositionByStrategy">
                  <div class="card-body__chart card-body__chart--hauto">
                    <PieChart
                        :view="'withTable'"
                        :loading="loading.composition"
                        :hideQuantity="true"
                        :series="[
                        {
                          name: t('dashboardClient.portfolio_layout'),
                          data: compositionByStrategy
                        }
                      ]"
                    ></PieChart>
                  </div>
                </div>
              </div>
            </div>
            <div class="tab-pane fade" id="dashboard-tab-composition-by-assets-content" role="tabpanel" aria-labelledby="dashboard-tab-composition-by-assets">
              <div class="card">
                <div class="card-body card-body--only-chart" v-if="compositionByAsset">
                  <div class="card-body__chart card-body__chart--hauto">
                    <PieChart
                        :view="'withTable'"
                        :loading="loading.composition"
                        :series="[
                        {
                          name: t('dashboardClient.portfolio_layout'),
                          data: compositionByAsset
                        }
                      ]"
                    ></PieChart>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- dluhopisy se načtou až po prvním přepnutí a pak zůstanou vykreslené (bez opakovaného načítání) -->
    <DashboardClientBonds v-if="bondsVisited && bondStats" v-show="activeView === 'BONDS'" :stats="bondStats"/>
  </div>
</template>
